import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { lotApi } from '../utils/lotApi'
import type { Lot } from '../types/lot'

type LotKey = `${string}::${string}`

type LotState = {
  lots: Lot[]
  loading: boolean
  error: string | null
}

type UseLotsOptions = {
  farmId?: string
  productionUnitId?: string
  enabled?: boolean
}

type CacheEntry = {
  lots: Lot[]
  fetchedAt: number
}

const lotCache = new Map<LotKey, CacheEntry>()
const generateKey = (farmId?: string, productionUnitId?: string): LotKey => (
  `${farmId ?? ''}::${productionUnitId ?? ''}`
)

export const invalidateLotCache = (options: { farmId?: string; productionUnitId?: string } = {}) => {
  const { farmId, productionUnitId } = options
  if (!farmId && !productionUnitId) {
    lotCache.clear()
    return
  }

  Array.from(lotCache.keys()).forEach((key) => {
    const [cachedFarmId, cachedUnitId] = key.split('::')
    if (farmId && cachedFarmId !== (farmId ?? '')) {
      return
    }
    if (productionUnitId && cachedUnitId !== (productionUnitId ?? '')) {
      return
    }
    lotCache.delete(key)
  })
}

export const useLots = (options: UseLotsOptions = {}) => {
  const { farmId, productionUnitId, enabled = true } = options
  const key = useMemo(() => generateKey(farmId, productionUnitId), [farmId, productionUnitId])
  const [state, setState] = useState<LotState>(() => {
    const cached = lotCache.get(key)
    return {
      lots: cached?.lots ?? [],
      loading: enabled && !cached,
      error: null
    }
  })
  const active = enabled && Boolean(farmId || productionUnitId || key === '::')
  const requestId = useRef(0)

  const fetchLots = useCallback(async () => {
    if (!active) {
      setState((prev) => ({ ...prev, loading: false }))
      return
    }

    const nextId = requestId.current + 1
    requestId.current = nextId
    setState((prev) => ({ ...prev, loading: true, error: null }))

    try {
      const response = await lotApi.list({
        farmId: farmId || undefined,
        productionUnitId: productionUnitId || undefined
      })
      if (requestId.current !== nextId) return
      lotCache.set(key, { lots: response, fetchedAt: Date.now() })
      setState({ lots: response, loading: false, error: null })
    } catch (error) {
      if (requestId.current !== nextId) return
      const message = error instanceof Error ? error.message : 'Unable to load lots.'
      setState((prev) => ({ ...prev, loading: false, error: message }))
    }
  }, [active, farmId, productionUnitId, key])

  useEffect(() => {
    const cached = lotCache.get(key)
    if (cached) {
      setState({ lots: cached.lots, loading: false, error: null })
      return
    }
    if (!active) {
      setState({ lots: [], loading: false, error: null })
      return
    }
    void fetchLots()
  }, [key, active, fetchLots])

  const upsertLot = useCallback((lot: Lot) => {
    const targetKey = generateKey(lot.farmId, lot.productionUnitId)
    const entry = lotCache.get(targetKey)
    const nextLots = entry ? [...entry.lots] : []
    const existingIndex = nextLots.findIndex((item) => item.id === lot.id)
    if (existingIndex >= 0) {
      nextLots[existingIndex] = lot
    } else {
      nextLots.push(lot)
    }
    lotCache.set(targetKey, { lots: nextLots, fetchedAt: Date.now() })
    if (targetKey === key) {
      setState({ lots: nextLots, loading: false, error: null })
    }
  }, [key])

  const removeLot = useCallback((lot: Lot) => {
    const targetKey = generateKey(lot.farmId, lot.productionUnitId)
    const entry = lotCache.get(targetKey)
    if (!entry) return
    const nextLots = entry.lots.filter((item) => item.id !== lot.id)
    lotCache.set(targetKey, { lots: nextLots, fetchedAt: Date.now() })
    if (targetKey === key) {
      setState({ lots: nextLots, loading: false, error: null })
    }
  }, [key])

  return {
    lots: state.lots,
    loading: state.loading,
    error: state.error,
    refresh: fetchLots,
    upsertLot,
    removeLot
  }
}
